import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { connectSocket } from '../../utils/socket';
import QUIZ_THEMES from '../../utils/quizThemes';

const OPTION_KEYS = ['a', 'b', 'c', 'd'];

export default function SpectatorPage() {
  const { sessionId } = useParams();
  const { i18n } = useTranslation();
  const lang = i18n.language === 'al' ? 'al' : 'de';

  const [status, setStatus] = useState('waiting');
  const [question, setQuestion] = useState(null);
  const [qIndex, setQIndex] = useState(0);
  const [qTotal, setQTotal] = useState(0);
  const [timeLeft, setTimeLeft] = useState(0);
  const [timeLimitSec, setTimeLimitSec] = useState(20);
  const [correctOption, setCorrectOption] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [bgColor, setBgColor] = useState('dark');
  const [bgImage, setBgImage] = useState(null);
  const timerRef = useRef(null);

  const theme = QUIZ_THEMES[bgColor] || QUIZ_THEMES.dark;
  const outerBg = bgImage
    ? { backgroundImage: `url(${bgImage})`, backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed' }
    : { background: theme.bg };
  const liveText = bgImage ? '#fff' : theme.text;

  useEffect(() => {
    const socket = connectSocket();
    socket.emit('spectator:join', { sessionId });

    socket.on('session:state', ({ status: s, bgColor: bc, bgImage: bi }) => {
      setStatus(s);
      if (bc) setBgColor(bc);
      if (bi) setBgImage(bi);
    });

    socket.on('quiz:started', ({ bgColor: bc, bgImage: bi }) => {
      setStatus('active');
      if (bc) setBgColor(bc);
      if (bi) setBgImage(bi);
    });

    socket.on('question:start', ({ index, total, timeLimitSec: tl, question: q }) => {
      setQuestion(q);
      setQIndex(index);
      setQTotal(total);
      setTimeLimitSec(tl);
      setTimeLeft(tl);
      setCorrectOption(null);
      setStatus('active');

      if (timerRef.current) clearInterval(timerRef.current);
      const interval = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) { clearInterval(interval); return 0; }
          return prev - 1;
        });
      }, 1000);
      timerRef.current = interval;
    });

    socket.on('question:end', ({ correctOption: co, leaderboard: lb }) => {
      setCorrectOption(co);
      setLeaderboard(lb);
    });

    socket.on('quiz:finished', ({ leaderboard: lb }) => {
      setLeaderboard(lb);
      setStatus('finished');
    });

    return () => {
      socket.off('session:state');
      socket.off('quiz:started');
      socket.off('question:start');
      socket.off('question:end');
      socket.off('quiz:finished');
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [sessionId]);

  const getText = (q, field) => q[`${field}_${lang}`] || q[`${field}_de`] || '';

  const timerPct = timeLimitSec > 0 ? (timeLeft / timeLimitSec) * 100 : 0;
  const overlayStyle = bgImage
    ? { minHeight: '100vh', background: 'rgba(0,0,0,0.55)', padding: '32px 24px', color: '#fff' }
    : { minHeight: '100vh', padding: '32px 24px', color: theme.text };

  if (status === 'waiting') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', ...outerBg }}>
        <div style={{ textAlign: 'center', color: liveText }}>
          <div className="spinner" style={{ margin: '0 auto 16px' }} />
          <p style={{ fontSize: 18 }}>Warte auf Quiz-Start...</p>
          <p style={{ fontSize: 13, opacity: 0.6, marginTop: 8 }}>Zuschauer-Modus</p>
        </div>
      </div>
    );
  }

  if (status === 'finished') {
    return (
      <div style={{ minHeight: '100vh', ...outerBg }}>
        <div style={overlayStyle}>
          <div style={{ maxWidth: 560, margin: '0 auto', textAlign: 'center' }}>
            <h1 style={{ fontSize: 28, fontWeight: 700, color: liveText, marginBottom: 8 }}>Quiz beendet</h1>
            <p style={{ color: `${liveText}99`, marginBottom: 32 }}>Ergebnisse</p>
            {leaderboard.map((p, i) => (
              <div key={i} style={{
                display: 'flex', alignItems: 'center', gap: 14, padding: '12px 16px',
                background: bgImage ? 'rgba(255,255,255,0.1)' : theme.surface,
                borderRadius: 10, marginBottom: 8,
                border: `1px solid ${bgImage ? 'rgba(255,255,255,0.15)' : theme.border}`,
              }}>
                <div style={{ width: 30, textAlign: 'center', fontWeight: 800, fontSize: 18, color: i === 0 ? '#C9A84C' : i === 1 ? '#aaa' : i === 2 ? '#C17B5C' : `${liveText}66` }}>
                  {p.rank}
                </div>
                <div style={{ flex: 1, color: liveText }}>{p.firstName} {p.lastName}</div>
                <div style={{ fontWeight: 700, color: theme.accent, fontSize: 17 }}>{p.score}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', ...outerBg }}>
      <div style={overlayStyle}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>

          {/* Spectator badge */}
          <div style={{ textAlign: 'center', marginBottom: 20 }}>
            <span style={{ background: bgImage ? 'rgba(255,255,255,0.15)' : theme.surface, color: `${liveText}99`, padding: '4px 14px', borderRadius: 100, fontSize: 12 }}>
              ZUSCHAUER
            </span>
          </div>

          {/* Question progress + timer */}
          {question && (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                <span style={{ fontSize: 13, color: `${liveText}99` }}>Frage {qIndex + 1} / {qTotal}</span>
                <span style={{ fontSize: 22, fontWeight: 800, color: timerPct < 30 ? '#E05C4B' : liveText }}>{timeLeft}s</span>
              </div>
              <div style={{ height: 8, background: bgImage ? 'rgba(255,255,255,0.2)' : theme.surface, borderRadius: 4, overflow: 'hidden', marginBottom: 24 }}>
                <div style={{
                  height: '100%', borderRadius: 4, transition: 'width 1s linear',
                  width: `${timerPct}%`,
                  background: timerPct < 30 ? '#E05C4B' : timerPct < 50 ? '#C9A84C' : theme.accent,
                }} />
              </div>

              {/* Question text */}
              <div style={{
                background: bgImage ? 'rgba(0,0,0,0.45)' : theme.surface,
                borderRadius: 16, padding: '28px 24px', marginBottom: 20,
                border: `1px solid ${bgImage ? 'rgba(255,255,255,0.15)' : theme.border}`,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 14,
              }}>
                {question.image_path && (
                  <img
                    src={`/uploads/${question.image_path}`}
                    alt=""
                    style={{ display: 'block', maxWidth: '100%', maxHeight: 240, borderRadius: 10, objectFit: 'contain' }}
                  />
                )}
                <p style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.4, color: liveText, textAlign: 'center', margin: 0 }}>
                  {getText(question, 'text')}
                </p>
              </div>

              {/* Options */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                {OPTION_KEYS.filter(k => question[`option_${k}_de`]).map(key => {
                  const isCorrect = correctOption && key === correctOption;
                  return (
                    <div key={key} style={{
                      padding: '18px 16px',
                      borderRadius: 12,
                      border: `2px solid ${isCorrect ? '#5B8A75' : (bgImage ? 'rgba(255,255,255,0.2)' : theme.border)}`,
                      background: isCorrect ? '#5B8A75' : (bgImage ? 'rgba(255,255,255,0.08)' : theme.surface),
                      color: isCorrect ? '#fff' : liveText,
                      transition: 'all 0.3s',
                      fontSize: 15,
                    }}>
                      <span style={{ display: 'block', fontSize: 11, opacity: 0.6, marginBottom: 4 }}>{key.toUpperCase()}</span>
                      {getText(question, `option_${key}`)}
                    </div>
                  );
                })}
              </div>
            </>
          )}

          {/* Leaderboard (shown after question:end) */}
          {correctOption && leaderboard.length > 0 && (
            <div style={{ marginTop: 28, background: bgImage ? 'rgba(0,0,0,0.45)' : theme.surface, borderRadius: 14, padding: 20, border: `1px solid ${bgImage ? 'rgba(255,255,255,0.15)' : theme.border}` }}>
              <h3 style={{ fontSize: 14, color: `${liveText}99`, marginBottom: 14 }}>Rangliste</h3>
              {leaderboard.slice(0, 10).map((p, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '8px 0', borderBottom: i < leaderboard.length - 1 ? `1px solid ${bgImage ? 'rgba(255,255,255,0.08)' : theme.border}` : 'none' }}>
                  <div style={{ width: 28, textAlign: 'center', fontWeight: 700, color: i === 0 ? '#C9A84C' : i === 1 ? '#aaa' : i === 2 ? '#C17B5C' : `${liveText}55` }}>
                    {p.rank}
                  </div>
                  <div style={{ flex: 1, color: liveText }}>{p.firstName} {p.lastName}</div>
                  <div style={{ fontWeight: 700, color: theme.accent }}>{p.score}</div>
                </div>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}
